import type { ThreadContext, ThreadStatusTone, TradeThreadContext } from 'types/interfaces/thread';
import { isCaseContext, isOfferContext, isTradeContext } from 'types/interfaces/thread';

/**
 * Status chips for the rail and the mobile strip, mirroring the tones the
 * backend uses for inbox rows (backend plan comment, status tones).
 */
export interface StatusChip {
  label: string;
  tone: ThreadStatusTone;
}

export const TRADE_STATUS_CHIP: Record<string, StatusChip> = {
  READY: { label: 'Ready to ship', tone: 'blue' },
  PENDING_FUNDING: { label: 'Awaiting funding', tone: 'purple' },
  TRANSIT: { label: 'Shipped', tone: 'orange' },
  COMPLETED: { label: 'Completed', tone: 'green' },
  CANCELLED: { label: 'Cancelled', tone: 'grey' },
};

export const CASE_STATUS_CHIP: Record<string, StatusChip> = {
  UNREAD: { label: 'Open', tone: 'orange' },
  READ: { label: 'Open', tone: 'orange' },
  IN_PROGRESS: { label: 'Admin reviewing', tone: 'blue' },
  RESOLVED: { label: 'Resolved', tone: 'green' },
  CLOSED: { label: 'Closed', tone: 'grey' },
};

export const OFFER_STATUS_CHIP: Record<string, StatusChip> = {
  PENDING: { label: 'Pending', tone: 'orange' },
  ACCEPTED: { label: 'Accepted', tone: 'green' },
  DECLINED: { label: 'Declined', tone: 'red' },
  EXPIRED: { label: 'Expired', tone: 'grey' },
  CANCELLED: { label: 'Withdrawn', tone: 'grey' },
  FAILED: { label: 'Failed', tone: 'red' },
};

const OPEN_CASE_STATUSES = new Set(['UNREAD', 'READ', 'IN_PROGRESS']);

/** True while the trade's case is UNREAD, READ or IN_PROGRESS (change 2). */
export const hasOpenCase = (context: Pick<TradeThreadContext, 'caseSummary'>): boolean =>
  !!context.caseSummary && OPEN_CASE_STATUSES.has(context.caseSummary.status.toUpperCase());

/** The chip the inbox row carries on a trade with an open case. */
export const CASE_OPEN_CHIP: StatusChip = { label: 'Case open', tone: 'orange' };

/** Unknown statuses render their raw value in grey rather than nothing. */
export const statusChipFor = (context: ThreadContext | undefined): StatusChip | null => {
  if (isTradeContext(context)) {
    if (hasOpenCase(context)) return CASE_OPEN_CHIP;
    return TRADE_STATUS_CHIP[context.status.toUpperCase()] ?? { label: context.status, tone: 'grey' };
  }
  if (isCaseContext(context)) {
    return CASE_STATUS_CHIP[context.status.toUpperCase()] ?? { label: context.status, tone: 'grey' };
  }
  if (isOfferContext(context)) {
    return OFFER_STATUS_CHIP[context.status.toUpperCase()] ?? { label: context.status, tone: 'grey' };
  }
  return null;
};
